export type AllocationMethod = 'VALUE' | 'QUANTITY' | 'WEIGHT';

export interface LandedCostLine {
  quantity: number;
  unitCost: number; // in the PO's own currency
  unitWeight?: number; // kg per unit
}

// Same sharing-out as the backend's purchase-orders/landed-cost.ts (this is
// the live preview; the backend recalculates and stores the real figures on
// save). Goods are in the PO currency and converted to GBP with
// `exchangeRate`; charges are in GBP.
export function computeLandedCosts(
  lines: LandedCostLine[],
  totalCharges: number,
  method: AllocationMethod,
  exchangeRate = 1,
): { allocation: number; landedUnitCost: number; goodsPerUnit: number }[] & { fellBack?: boolean } {
  const rate = exchangeRate > 0 ? exchangeRate : 1;
  const basisOf = (line: LandedCostLine, m: AllocationMethod) => {
    const qty = Number(line.quantity) || 0;
    if (m === 'QUANTITY') return qty;
    if (m === 'WEIGHT') return qty * (Number(line.unitWeight) || 0);
    return qty * (Number(line.unitCost) || 0) * rate;
  };

  let effective = method;
  let totalBasis = lines.reduce((sum, l) => sum + basisOf(l, effective), 0);
  let fellBack = false;
  if (totalBasis <= 0 && totalCharges > 0) {
    effective = 'QUANTITY';
    totalBasis = lines.reduce((sum, l) => sum + basisOf(l, effective), 0);
    fellBack = method !== 'QUANTITY';
  }

  const result = lines.map((line) => {
    const qty = Number(line.quantity) || 0;
    const allocation = totalBasis > 0 ? (totalCharges * basisOf(line, effective)) / totalBasis : 0;
    const goodsPerUnit = (Number(line.unitCost) || 0) * rate;
    return {
      allocation,
      goodsPerUnit,
      landedUnitCost: goodsPerUnit + (qty > 0 ? allocation / qty : 0),
    };
  }) as { allocation: number; landedUnitCost: number; goodsPerUnit: number }[] & { fellBack?: boolean };
  result.fellBack = fellBack;
  return result;
}
